import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import { FormEvent, useState } from "react";
import { ProfileResponse } from "@/utils/Hooks";
import { ProfileData } from "@/types/shared.types";
import { KeyedMutator } from "swr";
import EntryModal from "@/components/dialog/EntryModal";
import { TextField, Typography } from "@mui/material";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import BorrowModal from "@/components/dialog/BorrowModal";
import ExitModal from "@/components/dialog/ExitModal";

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: {
    xs: '100%',
    sm: 400
  },
  height: {
    xs: '100%',
    sm: 'auto'
  },
  width: '100%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 2,
  borderRadius: {
    xs: 0,
    sm: 2
  }
};

type VisitorModalProps = {
  accessToken: string,
  open: boolean,
  setOpen: (open: boolean) => void,
  profile?: {
    data: ProfileData | undefined;
    mutate: KeyedMutator<ProfileResponse> | undefined
  } | undefined

}

export default function VisitorModal({ accessToken, open, setOpen, profile }: VisitorModalProps) {
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [modalContent, setModalContent] = useState('code');
  const [visitorToken, setVisitorToken] = useState('');
  const [koleksi, setKoleksi] = useState('');

  const handleEntry = (token: string) => {
    setVisitorToken(token);
    if (profile?.data?.kunjungan) {
      setModalContent('exit');
    } else {
      setModalContent('entry');
    }
  };

  const handleBorrow = (koleksi: any) => {
    setKoleksi(koleksi);
    setModalContent('borrow');
  };

  const handleRootBack = () => {
    setModalContent('code');
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500
        }
      }}
      onTransitionExited={
        () => {
          setModalContent('code');
          setVisitorToken('');
          setKoleksi('');
        }
      }
    >
      <Fade in={open}>
        <Box sx={style}>
          {modalContent === 'code' &&
            <CodePage accessToken={accessToken} entryCallback={handleEntry} borrowCallback={handleBorrow}/>}
          {modalContent === 'entry' &&
            <EntryModal
              visitorToken={visitorToken}
              accessToken={accessToken}
              setOpen={setOpen}
              handleRootBack={handleRootBack}
            />}
          {modalContent === 'exit' &&
            <ExitModal
              visitorToken={visitorToken}
              accessToken={accessToken}
              setOpen={setOpen}
              profile={profile}
              handleRootBack={handleRootBack}
            />}
          {modalContent === 'borrow' &&
            <BorrowModal
              koleksi={koleksi}
              accessToken={accessToken}
              setOpen={setOpen}
              profile={profile}
              handleRootBack={handleRootBack}
            />}
        </Box>
      </Fade>
    </Modal>
  );
}

const getVisitorToken = async (accessToken: string, code: string) => {
  const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/v2/visitor/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      code
    })
  });

  const { token, message } = await response.json();

  return { result: token, message };
};

const getKoleksi = async (accessToken: string, code: string) => {
  const url = new URL(`${process.env.NEXT_PUBLIC_API_URL}/v2/peminjaman/preview`);
  url.searchParams.append('code', code);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    }
  });

  const { koleksi, message } = await response.json();

  return { result: koleksi, message };
};


class EntryModalPageProps {
  accessToken: string;
  entryCallback: (token: string) => void;
  borrowCallback: (koleksi: any) => void;
}

function CodePage({ accessToken, entryCallback, borrowCallback }: EntryModalPageProps) {
  const [code, setCode] = useState('');
  const [helperText, setHelperText] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleCodeSubmit = async (e: FormEvent) => {
    e?.preventDefault();
    submitCode(code);
  };

  const submitCode = async (code) => {
    setLoading(true);

    if (code.length === 6) {
      const { result } = await getVisitorToken(accessToken, code);

      if (result) {
        entryCallback(result);
        setLoading(false);
      }
    }

    const { result, message } = await getKoleksi(accessToken, code);

    if (result) {
      borrowCallback(result);
      setLoading(false);
    }

    setLoading(false);

    setHelperText(message);
    setError(true);
  };

  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}>
    <Typography component="h2" sx={{
      fontSize: 20,
      fontWeight: 700
    }}>Scan</Typography>
    <Box sx={{
      width: "100%",
      height: "100%"
    }}>
      <style>
        {`video {
          width: 100%;
          height: 100%;
        }`}
      </style>
      <BarcodeScannerComponent
        width={500}
        height={0}
        onUpdate={(err, result) => {
          if (!result) return;
          const code = result.getText();
          setCode(code);
          submitCode(code);
        }}
      />
    </Box>
    <Typography
    >
      Atau, masukan kode secara manual.
    </Typography>
    <form onSubmit={handleCodeSubmit}>
      <TextField
        id="code-input"
        label="Kode"
        variant="filled"
        error={error}
        helperText={helperText}
        value={code}
        disabled={loading}
        onChange={e => {
          setError(false);
          setHelperText('');
          setCode(e.target.value);
        }}
        sx={{
          marginBottom: 2
        }}
        fullWidth/>
      <Button
        fullWidth
        variant="contained"
        onClick={handleCodeSubmit}
        type="submit"
        size="large"
        disabled={loading}
      >
        Kirim
      </Button>
    </form>
  </Box>;
}
